import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import BackButton from '../components/BackButton';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { fillablePdf, chooseFillMode, setNextFillMode, peekFillMode, blankCopy, setPdfPatient } from '../utils/fillable';
import { buildMedicationListPdf } from '../utils/medicationListPdf';
import { buildFacesheetPdf } from '../utils/facesheetPdf';
import { fetchCms485Blob, downloadBlob, savePdfToPackages } from '../utils/documentExport';
import { EXACT_FORMS, fetchExactPdfForExport } from '../utils/exactForms';
import { buildOrderPdf } from '../utils/orderPdf';

const ORDER_TYPES = { physician_orders: "Physician's Order", doctors_orders: "Doctor's Order", medication_order: 'Medication Order', therapy_order: 'Therapy Order', dme_order: 'Medical Supply / DME Order', wound_care_order: 'Wound Care Order' };
const ORDER_KIND = { therapy_order: 'therapy', dme_order: 'dme', medication_order: 'medication', wound_care_order: 'wound' };
const MED_ORDERS = ['physician_orders', 'doctors_orders', 'medication_order'];
import { SendForSignatureModal } from '../components/SendForSignatureModal';
import { SignatureTracker } from '../components/SignatureTracker';
import { STAFF_DOCS } from '../components/timeclock/staffDocSpecs';
import { drawTitledHeader, loadAgencyLogo } from '../utils/agencyInfo';
import { isDisciplineOrders, isStringList, DisciplineOrdersTable, BulletList, drawPdfValue } from '../utils/documentValueRender';
import { 
  FileText, Upload, Wand2, FileUp, History, Loader2, 
  CheckCircle2, AlertCircle, Download, Eye, Trash2, 
  FileType, Brain, Sparkles, ChevronDown, ChevronRight, Printer,
  Save, Plus, X, Star, ArrowLeft,
  FolderPlus, Send, RefreshCw, Pill
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Batch Document Generation Templates
const DOCUMENT_TEMPLATES = {
  new_patient_admission: {
    name: 'New Patient Admission Package',
    description: 'Complete admission documentation for new patients',
    icon: '📋',
    color: 'from-amber-500 to-orange-500',
    documents: ['facesheet', 'admission_consent_exact', 'admission_packet_exact', 'soc_roc'],
    estimatedTime: '2-3 min'
  },
  recertification_package: {
    name: 'Recertification Package',
    description: '60-day recertification documentation',
    icon: '🔄',
    color: 'from-green-500 to-emerald-500',
    documents: ['recertification', 'poc_485', 'physician_orders'],
    estimatedTime: '2-3 min'
  },
  discharge_package: {
    name: 'Discharge Package',
    description: 'Complete discharge documentation set',
    icon: '🏠',
    color: 'from-blue-500 to-cyan-500',
    documents: ['discharge', 'physician_orders', 'face_to_face'],
    estimatedTime: '2-3 min'
  },
  skilled_nursing_visit: {
    name: 'Skilled Nursing Visit',
    description: 'Documentation for skilled nursing visits',
    icon: '💉',
    color: 'from-pink-500 to-rose-500',
    documents: ['nurses_notes', 'wound_care', 'wound_care_order', 'wound_watfs', 'iv_therapy'],
    estimatedTime: '1-2 min'
  },
  therapy_documentation: {
    name: 'Therapy Documentation',
    description: 'PT/OT/ST therapy visit documentation',
    icon: '🏃',
    color: 'from-purple-500 to-violet-500',
    documents: ['pt_evaluation', 'ot_evaluation', 'st_evaluation', 'msw_evaluation', 'dietitian_evaluation', 'therapist_notes', 'physician_orders'],
    estimatedTime: '1-2 min'
  },
  home_health_aide_visit: {
    name: 'Home Health Aide Visit',
    description: 'HHA visit documentation package',
    icon: '🤝',
    color: 'from-teal-500 to-cyan-500',
    documents: ['hha_notes', 'lvn_notes'],
    estimatedTime: '1 min'
  },
  comprehensive_assessment: {
    name: 'Comprehensive Assessment',
    description: 'Full OASIS assessment package',
    icon: '📊',
    color: 'from-indigo-500 to-blue-500',
    documents: ['oasis_assessment', 'soc_roc', 'poc_485', 'face_to_face'],
    estimatedTime: '3-4 min'
  },
  physician_certification: {
    name: 'Physician Certification',
    description: 'Physician orders and certification documents',
    icon: '👨‍⚕️',
    color: 'from-red-500 to-pink-500',
    documents: ['physician_orders', 'doctors_orders', 'face_to_face', 'poc_485'],
    estimatedTime: '2-3 min'
  }
};

// Document type configurations - organized by category
const DOCUMENT_CATEGORIES = {
  admission: {
    name: 'Admission & Consent',
    color: 'bg-amber-50 border-amber-200'
  },
  oasis: {
    name: 'OASIS Assessments',
    color: 'bg-green-50 border-green-200'
  },
  medication: {
    name: 'Medication Management',
    color: 'bg-teal-50 border-teal-200'
  },
  orders: {
    name: 'Physician Orders (all order forms)',
    color: 'bg-red-50 border-red-200'
  },
  clinical: {
    name: 'Clinical Documents',
    color: 'bg-purple-50 border-purple-200'
  },
  nursing: {
    name: 'Nursing Documentation',
    color: 'bg-blue-50 border-blue-200'
  },
  therapy: {
    name: 'Therapy Documentation',
    color: 'bg-pink-50 border-pink-200'
  },
  staff_hub: {
    name: 'Staff Hub',
    color: 'bg-slate-50 border-slate-300'
  }
};

const DOCUMENT_TYPES = {
  // Admission & Consent
  facesheet: {
    name: 'Patient Facesheet',
    description: 'Demographics and administrative summary',
    icon: FileText,
    color: 'bg-amber-100 text-amber-700',
    category: 'admission'
  },
  admission_consent: {
    hidden: true,
    name: 'Admission Consent',
    description: 'Patient admission and consent forms',
    icon: FileText,
    color: 'bg-amber-100 text-amber-700',
    category: 'admission'
  },
  admission_consent_exact: {
    name: 'Admission Consent (IVORY form)',
    description: "Your agency's own 2-page Admission Consent, word for word; only the boxes are filled in",
    icon: FileText,
    color: 'bg-amber-100 text-amber-700',
    category: 'admission'
  },
  admission_packet_exact: {
    name: 'Admission Packet HHA (IVORY form)',
    description: "Your agency's own 49-page Fillable Admission Packet, word for word; only the boxes are filled in",
    icon: FileText,
    color: 'bg-amber-100 text-amber-700',
    category: 'admission'
  },
  // OASIS Assessments
  soc_roc: {
    name: 'SOC/ROC Assessment (OASIS-E2)',
    description: 'OASIS-E2 Start of Care / Resumption of Care, eff. 04/01/2026',
    icon: FileType,
    color: 'bg-green-100 text-green-700',
    category: 'oasis'
  },
  discharge: {
    name: 'Discharge Assessment (OASIS-E2)',
    description: 'OASIS-E2 Discharge from Agency, eff. 04/01/2026',
    icon: FileText,
    color: 'bg-green-100 text-green-700',
    category: 'oasis'
  },
  recertification: {
    name: 'Recertification Assessment (OASIS-E2)',
    description: 'OASIS-E2 Follow-up (M0100 = 4), eff. 04/01/2026',
    icon: FileText,
    color: 'bg-green-100 text-green-700',
    category: 'oasis'
  },
  oasis_assessment: {
    name: 'OASIS Assessment (OASIS-E2)',
    description: 'OASIS-E2 items for the time point in the source (M0100)',
    icon: FileType,
    color: 'bg-green-100 text-green-700',
    category: 'oasis'
  },
  // Medication Management
  medication_list: {
    name: 'Medication List',
    description: 'Routine and PRN medications, dosages, allergies',
    icon: FileText,
    color: 'bg-teal-100 text-teal-700',
    category: 'medication'
  },
  // Clinical Documents
  poc_485: {
    name: '485 Plan of Care',
    description: 'CMS 485 Certification and Plan of Care',
    icon: FileText,
    color: 'bg-purple-100 text-purple-700',
    category: 'clinical'
  },
  physician_orders: {
    name: "Physician's Order",
    description: 'Verbal or written order: numbered order lines, read-back, physician signature',
    icon: FileText,
    color: 'bg-red-100 text-red-700',
    category: 'orders'
  },
  doctors_orders: {
    name: "Doctor's Order",
    description: 'Medications, treatments, labs, diet and activity orders for signature',
    icon: FileText,
    color: 'bg-red-100 text-red-700',
    category: 'orders'
  },
  face_to_face: {
    name: 'Face to Face Encounter',
    description: 'Physician face-to-face certification',
    icon: FileText,
    color: 'bg-purple-100 text-purple-700',
    category: 'clinical'
  },
  // Nursing Documentation
  nurses_notes: {
    name: 'Nurses Notes',
    description: 'Comprehensive skilled nursing visit notes',
    icon: FileText,
    color: 'bg-blue-100 text-blue-700',
    category: 'nursing'
  },
  hha_notes: {
    name: 'Home Health Aide Notes',
    description: 'Home health aide visit documentation',
    icon: FileText,
    color: 'bg-blue-100 text-blue-700',
    category: 'nursing'
  },
  lvn_notes: {
    name: 'LVN Notes',
    description: 'Licensed vocational nurse visit notes',
    icon: FileText,
    color: 'bg-blue-100 text-blue-700',
    category: 'nursing'
  },
  wound_care: {
    name: 'Wound Care Flow Sheet',
    description: 'Wound tracking and treatment documentation',
    icon: FileText,
    color: 'bg-blue-100 text-blue-700',
    category: 'nursing'
  },
  iv_therapy: {
    name: 'IV Therapy Administration',
    description: 'IV therapy administration documentation',
    icon: FileText,
    color: 'bg-blue-100 text-blue-700',
    category: 'nursing'
  },
  // Therapy Documentation
  therapist_notes: {
    name: 'Therapist Notes',
    description: 'PT/OT/ST therapy documentation',
    icon: FileText,
    color: 'bg-pink-100 text-pink-700',
    category: 'therapy'
  },
  pt_evaluation: {
    name: 'Physical Therapy Initial Evaluation',
    description: 'PT evaluation: homebound reason, PLOF, pain, vitals, gait, balance tests, goals and plan',
    icon: FileText,
    color: 'bg-pink-100 text-pink-700',
    category: 'therapy'
  },
  ot_evaluation: {
    name: 'Occupational Therapy Initial Evaluation',
    description: 'OT evaluation: homebound reason, PLOF, ADLs/IADLs, UE & hand function, vision, home safety, goals and plan',
    icon: FileText,
    color: 'bg-pink-100 text-pink-700',
    category: 'therapy'
  },
  msw_evaluation: {
    name: 'Social Work Evaluation',
    description: 'MSW evaluation: living situation, support, emotional status, resources, advance directives, safety',
    icon: FileText,
    color: 'bg-pink-100 text-pink-700',
    category: 'therapy'
  },
  dietitian_evaluation: {
    name: 'Dietitian Evaluation',
    description: 'Nutrition evaluation: weight, diet & intake, labs, risks, estimated needs, plan',
    icon: FileText,
    color: 'bg-pink-100 text-pink-700',
    category: 'therapy'
  },
  st_evaluation: {
    name: 'Speech Therapy Initial Evaluation',
    description: 'ST evaluation: homebound reason, communication, cognitive-linguistic, swallowing / diet, goals and plan',
    icon: FileText,
    color: 'bg-pink-100 text-pink-700',
    category: 'therapy'
  },
  medication_order: {
    name: 'Medication Order',
    description: 'New, changed or discontinued medications: one line each with dose, route, frequency and instructions',
    icon: FileText,
    color: 'bg-red-100 text-red-700',
    category: 'orders'
  },
  therapy_order: {
    name: 'Therapy Order',
    description: 'PT / OT / ST order: evaluate & treat, frequency, interventions, goals, precautions',
    icon: FileText,
    color: 'bg-red-100 text-red-700',
    category: 'orders'
  },
  dme_order: {
    name: 'Medical Supply / DME Order',
    description: 'Supplies and equipment: items, quantity, HCPCS, medical necessity, length of need, supplier',
    icon: FileText,
    color: 'bg-red-100 text-red-700',
    category: 'orders'
  },
  wound_care_order: {
    name: 'Wound Care Order',
    description: 'Physician wound care order: wound, cleanser, primary/secondary dressing, frequency, duration, SN visits',
    icon: FileText,
    color: 'bg-red-100 text-red-700',
    category: 'orders'
  },
  wound_watfs: {
    name: 'Wound Assessment & Treatment Flow Sheet (WATFS)',
    description: 'One per wound: etiology, stage, measurements, wound bed %, exudate, periwound, treatment',
    icon: FileText,
    color: 'bg-blue-100 text-blue-700',
    category: 'nursing'
  }
};
const VISIBLE_TYPES = Object.keys(DOCUMENT_TYPES).filter(k => !DOCUMENT_TYPES[k].hidden);

// CMS-style PDF templates for different document types
import { buildOasisPdf, fromGenerated, tpFromData, DOC_TP } from '../utils/oasisPdf';
import { buildClinicalFormPdf } from '../utils/clinicalFormPdf';
import { CLINICAL_FORMS } from '../utils/clinicalForms';
import { SendOrderSignModal } from '../components/SendOrderSignModal';
const clinicalLabel = (docType, key) => {
  const sec = CLINICAL_FORMS[docType]?.sections.find(x => x.fields.some(f => f.k === key));
  const l = sec?.fields.find(f => f.k === key).l;
  return l && /^tick all/i.test(l) ? sec.title : l;
};

const generateCMSStylePDF = (docType, docData, patientContext) => {
  if (docType === 'medication_list') return buildMedicationListPdf(docData?.data, patientContext);
  if (docType === 'facesheet') return buildFacesheetPdf(docData?.data, patientContext);
  if (CLINICAL_FORMS[docType]) {
    const d = docData?.data || {};
    return buildClinicalFormPdf(docType, { ...d, patientName: patientContext?.patientName || d.patientName, dateOfBirth: patientContext?.dateOfBirth || d.dateOfBirth, medicalRecordNumber: patientContext?.mrn || d.medicalRecordNumber },
      patientContext?.patientName ? { name: patientContext.patientName, dateOfBirth: patientContext.dateOfBirth, medicalRecordNumber: patientContext.mrn, primaryDiagnosis: patientContext.primaryDiagnosis, startOfCareDate: patientContext.startOfCareDate } : {});
  }
  const doc = fillablePdf({ format: 'letter' });
  doc.patientInfo = { name: patientContext.patientName, mrn: patientContext.mrn, dob: patientContext.dateOfBirth, dx: patientContext.primaryDiagnosis, admission: patientContext.startOfCareDate };
  const config = DOCUMENT_TYPES[docType];
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = 20;
  
  
  // Agency header (logo, name, address, contact, blue rule) and the document title
  drawTitledHeader(doc, config?.name || docType.toUpperCase());
  
  y = 45;
  doc.setTextColor(0, 0, 0);
  
  // Document info box
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(248, 249, 250);
  doc.rect(margin, y, pageWidth - (margin * 2), 25, 'FD');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Patient:', margin + 5, y + 8);
  doc.text('MRN:', margin + 80, y + 8);
  doc.text('DOB:', margin + 130, y + 8);
  doc.text('Generated:', margin + 5, y + 18);
  
  doc.setFont('helvetica', 'normal');
  doc.valueMode = true;
  doc.valueMaxX = margin + 128;
  doc.text(patientContext.patientName || docData?.data?.patientName || '', margin + 25, y + 8);
  doc.text(patientContext.mrn || docData?.data?.medicalRecordNumber || '', margin + 95, y + 8);
  doc.valueMaxX = pageWidth - margin - 2;
  doc.text(String(patientContext.dateOfBirth || docData?.data?.dateOfBirth || '').replace(/^(\d{4})-(\d{2})-(\d{2}).*$/, '$2/$3/$1'), margin + 145, y + 8);
  doc.valueMode = false;
  doc.valueMaxX = undefined;
  doc.text(new Date().toLocaleString(), margin + 35, y + 18);
  
  y += 35;
  
  // Content section
  if (docData?.data && typeof docData.data === 'object') {
    Object.entries(docData.data).forEach(([key, value]) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      
      const spaced = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
      const formattedKey = spaced.replace(/\b\w/g, c => c.toUpperCase());
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`${formattedKey}:`, margin, y);
      
      doc.setFont('helvetica', 'normal');
      const ensureSpace = (cy, h) => {
        if (cy + h > 275) { doc.addPage(); return 20; }
        return cy;
      };
      doc.valueMode = true;
      if (isDisciplineOrders(key, value)) {
        y = drawPdfValue(doc, key, value, margin, y + 6, pageWidth - margin * 2, ensureSpace);
      } else {
        y = drawPdfValue(doc, key, value, margin + 50, y, pageWidth - margin - 65, ensureSpace);
      }
      doc.valueMode = false;
      y += 3;
    });
  }
  
  // Footer on every page
  const footerY = doc.internal.pageSize.getHeight() - 15;
  const totalPages = doc.getNumberOfPages();
  doc.autoFill = false;  // footer rule and labels are decoration, not writable fields
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('Generated by Ivory Home Health Care AI Document System', margin, footerY);
    if (docData?.confidence_score !== undefined) {
      doc.text(`Confidence: ${docData.confidence_score}%`, pageWidth / 2 + 20, footerY);
    }
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 20, footerY);
  }
  
  return doc;
};

// Generate PDF for all documents
const pdfFilename = (docType) => `${docType}_${new Date().toISOString().split('T')[0]}.pdf`;

const buildDocPdfBlob = async (docType, rawDocData, rawContext) => {
  const blank = peekFillMode() === 'blank';
  const docData = blank ? { ...rawDocData, data: blankCopy(rawDocData?.data), confidence_score: undefined } : rawDocData;
  const patientContext = blank ? {} : rawContext;
  await loadAgencyLogo();
  if (docType === 'poc_485') return fetchCms485Blob(docData?.data, patientContext);
  if (EXACT_FORMS[docType]) return fetchExactPdfForExport(docType, patientContext?.patientId, docData?.data);
  if (ORDER_TYPES[docType]) return buildOrderPdf(ORDER_TYPES[docType], docData?.data || {}, patientContext || {}, ORDER_KIND[docType]).output('blob');
  if (DOC_TP[docType]) {
    const data = docData?.data || {};
    let tp = tpFromData(data, DOC_TP[docType]);
    if (docType === 'soc_roc' && !['SOC', 'ROC'].includes(tp)) tp = 'SOC';
    const user = JSON.parse(localStorage.getItem('ivory_user') || '{}');
    const cat = await fetch(`${API_URL}/api/oasis-e2/catalog/${tp}`, { headers: { Authorization: `Bearer ${user.token || localStorage.getItem('token')}` } }).then(r => r.json());
    const pick = (re) => Object.entries(data).find(([k]) => re.test(k))?.[1];
    return buildOasisPdf(tp, cat.items || [], fromGenerated(cat.items || [], data), {
      patientName: patientContext?.patientName || data.patientName || pick(/^M0040/), dateOfBirth: patientContext?.dateOfBirth || pick(/^M0066/),
      mrn: patientContext?.mrn || pick(/^M0020/), assessmentDate: data.assessmentDate || pick(/^M0090/), clinicianName: data.clinicianName,
    }).output('blob');
  }
  return generateCMSStylePDF(docType, docData, patientContext).output('blob');
};

const generateAllPDFs = async (documents, patientContext) => {
  const mode = await chooseFillMode();
  if (!mode) return;
  for (const [docType, docData] of Object.entries(documents)) {
    if (docData?.generated && docData?.data) {
      setNextFillMode(mode);
      downloadBlob(await buildDocPdfBlob(docType, docData, patientContext), pdfFilename(docType));
    }
  }
};

const DocumentGeneratorPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('text'); // 'text' or 'file'
  const [sourceText, setSourceText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDocTypes, setSelectedDocTypes] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [signatureDoc, setSignatureDoc] = useState(null);
  const autoPicked = useRef(false);
  const [patientContext, setPatientContext] = useState({
    patientName: '',
    mrn: '',
    dateOfBirth: '',
    primaryDiagnosis: ''
  });
  const [generating, setGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [signOrder, setSignOrder] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedDocs, setExpandedDocs] = useState({});
  const [patients, setPatients] = useState([]);
  const [showTemplates, setShowTemplates] = useState(true);
  const [generatedDocs, setGeneratedDocs] = useState([]);

  const getToken = useCallback(() => {
    const user = localStorage.getItem('ivory_user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.token;
    }
    return localStorage.getItem('token');
  }, []);

  // Apply a template - selects the document types from the template
  const applyTemplate = (templateKey, isCustom = false) => {
    if (isCustom) {
      const template = customTemplates.find(t => t.id === templateKey);
      if (template) {
        setSelectedTemplate(`custom_${templateKey}`);
        setSelectedDocTypes(template.documents);
        toast.success(`Template "${template.name}" applied - ${template.documents.length} documents selected`);
        // Increment usage count
        incrementTemplateUsage(templateKey);
      }
    } else {
      const template = DOCUMENT_TEMPLATES[templateKey];
      if (template) {
        setSelectedTemplate(templateKey);
        setSelectedDocTypes(template.documents);
        toast.success(`Template "${template.name}" applied - ${template.documents.length} documents selected`);
      }
    }
  };

  // Clear template selection
  const clearTemplate = () => {
    setSelectedTemplate(null);
    setSelectedDocTypes([]);
  };

  // Custom template state
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateIcon, setNewTemplateIcon] = useState('📋');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Template icons for selection
  const templateIcons = ['📋', '📄', '📝', '🏥', '💉', '🩺', '💊', '🏠', '👨‍⚕️', '👩‍⚕️', '🔬', '📊'];

  // Fetch custom templates
  const fetchCustomTemplates = useCallback(async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/document-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCustomTemplates(data.templates);
      }
    } catch (error) {
      console.error('Error fetching custom templates:', error);
    }
  }, [getToken]);

  // Save custom template
  const saveCustomTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (selectedDocTypes.length === 0) {
      toast.error('Please select at least one document type');
      return;
    }

    setSavingTemplate(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/document-templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newTemplateName,
          description: newTemplateDescription,
          documents: selectedDocTypes,
          icon: newTemplateIcon
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Template saved successfully!');
        setShowSaveTemplateModal(false);
        setNewTemplateName('');
        setNewTemplateDescription('');
        setNewTemplateIcon('📋');
        fetchCustomTemplates();
      } else {
        toast.error(data.detail || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Delete custom template
  const deleteCustomTemplate = async (templateId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/document-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Template deleted');
        fetchCustomTemplates();
        if (selectedTemplate === `custom_${templateId}`) {
          clearTemplate();
        }
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  // Increment template usage
  const incrementTemplateUsage = async (templateId) => {
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/document-templates/${templateId}/use`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error incrementing usage:', error);
    }
  };

  // Fetch patients and custom templates on mount
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const token = getToken();
        const response = await fetch(`${API_URL}/api/patients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) {
          setPatients(data.patients);
        }
      } catch (error) {
        console.error('Error fetching patients:', error);
      }
    };
    fetchPatients();
    fetchCustomTemplates();
  }, [getToken, fetchCustomTemplates]);

  // Fetch generation history
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/document-generator/history?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setHistory(data.history);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const [regenPending, setRegenPending] = useState(false);

  const loadHistoryItem = (item) => {
    setSelectedDocTypes(item.documentTypes || []);
    autoPicked.current = false;
    setPatientContext({ patientName: '', mrn: '', dateOfBirth: '', primaryDiagnosis: '', ...(item.patientContext || {}) });
    setExpandedDocs(Object.fromEntries((item.documentTypes || []).map(dt => [dt, true])));
  };

  const openHistoryItem = (item) => {
    loadHistoryItem(item);
    setGenerationResult(item.result);
    toast.success(`Opened ${item.sourceType === 'file' ? item.fileName : 'text input'} from ${new Date(item.createdAt).toLocaleDateString()}`);
    setTimeout(() => document.querySelector('[data-testid="generation-results"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const regenerateHistoryItem = (item) => {
    loadHistoryItem(item);
    if (item.sourceType === 'text' && item.sourceText) {
      setActiveTab('text');
      setSourceText(item.sourceText);
      setRegenPending(true);
    } else {
      setActiveTab(item.sourceType === 'file' ? 'file' : 'text');
      toast.info(item.sourceType === 'file'
        ? `Document types and patient loaded. Upload "${item.fileName}" again and click Generate.`
        : 'Document types and patient loaded. The original text was not saved for this older entry; paste it and click Generate.', { duration: 7000 });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Template route mapping for interactive forms
  const getTemplateRoute = (docType) => {
    const templateRoutes = {
      'facesheet': '/dashboard/templates/face-sheet',
      'poc_485': '/dashboard/templates/cms-485',
      'soc_roc': '/dashboard/forms/soc-roc',
      'admission_consent': '/dashboard/admission/consent-verification',
      'admission_consent_exact': '/dashboard/forms/exact/admission_consent_exact',
      'admission_packet_exact': '/dashboard/forms/exact/admission_packet_exact',
      'nurses_notes': '/dashboard/skilled-nursing/nurses-notes',
      'discharge': '/dashboard/forms/discharge',
      'recertification': '/dashboard/forms/recertification',
      'oasis_assessment': '/dashboard/forms/oasis-e2',
      'physician_orders': '/dashboard/clinical/physician-orders',
      'pt_evaluation': '/dashboard/forms/clinical/pt_evaluation',
      'ot_evaluation': '/dashboard/forms/clinical/ot_evaluation',
      'st_evaluation': '/dashboard/forms/clinical/st_evaluation',
      'msw_evaluation': '/dashboard/forms/clinical/msw_evaluation',
      'dietitian_evaluation': '/dashboard/forms/clinical/dietitian_evaluation',
      'wound_watfs': '/dashboard/forms/clinical/wound_watfs',
      'wound_care_order': '/dashboard/forms/clinical/wound_care_order',
    };
    return templateRoutes[docType] || null;
  };

  // Use the patient already being worked on: selected patient, the name in the uploaded file / generated document, or the last opened chart
  const ensurePatient = async (docData, create = true, announce = true) => {
    const d = docData?.data || {};
    const m0040 = Object.entries(d).find(([k]) => k.startsWith('M0040'))?.[1];
    const real = (x) => { const t = String(x || '').trim(); return t && !/\b(unknown|not (available|provided|specified|stated)|unnamed|no name|none provided)\b|^(n\/?a|none|null|patient|name|-+)$/i.test(t) ? t : ''; };
    const others = Object.values(generationResult?.documents || {}).map(x => x?.data || {});
    const any = (k) => real(d[k]) || others.map(o => real(o[k])).find(Boolean) || '';
    const docName = real(d.patientName) || real(m0040) || real(d.firstName ? `${d.firstName} ${d.lastName || ''}` : '') || any('patientName');
    const norm = (x) => x.toLowerCase().replace(/[^a-z ]/g, ' ').split(/\s+/).filter(Boolean).sort().join(' ');
    if (patientContext?.patientId && (!docName || norm(docName) === norm(patientContext.patientName || ''))) return patientContext;
    const raw = docName || (patientContext?.patientId ? '' : real(patientContext?.patientName));
    const toCtx = (p) => {
      const ctx = { ...patientContext, patientId: p.id, patientName: `${p.firstName} ${p.lastName}`, mrn: p.medicalRecordNumber || p.id.slice(0, 8),
        dateOfBirth: p.dateOfBirth || '', primaryDiagnosis: p.primaryDiagnosis || '', startOfCareDate: p.startOfCareDate || '' };
      setPatientContext(ctx);
      setPdfPatient(p);
      autoPicked.current = true;
      localStorage.setItem('ivory_current_patient', p.id);
      if (announce) toast.info(`Using patient ${ctx.patientName}`);
      return ctx;
    };
    let list = patients;
    if (!list.length) {
      const r = await fetch(`${API_URL}/api/patients`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(x => x.json()).catch(() => ({}));
      list = r.patients || [];
      setPatients(list);
    }
    if (raw) {
      const match = list.find(p => norm(`${p.firstName} ${p.lastName}`) === norm(raw));
      if (match) return toCtx(match);
      if (!create) return null;
      const parts = raw.includes(',') ? raw.split(',').map(x => x.trim()).reverse() : raw.split(/\s+/);
      const res = await fetch(`${API_URL}/api/patients`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ firstName: parts[0] || raw, lastName: parts.slice(1).join(' ') || '-', dateOfBirth: any('dateOfBirth') || d['M0066 Birth Date'] || '',
          phone: any('phone'), address: any('address') || any('patientAddress'), insurance: any('insurance') || any('primaryInsurance'),
          primaryDiagnosis: typeof d.primaryDiagnosis === 'string' ? d.primaryDiagnosis : '' }),
      }).then(x => x.json()).catch(() => ({}));
      if (res.patient) {
        setPatients(prev => [...prev, res.patient]);
        toast.success(`New patient ${res.patient.firstName} ${res.patient.lastName} created from this document`);
        return toCtx(res.patient);
      }
    }
    if (!create) return null;
    const last = list.find(p => p.id === localStorage.getItem('ivory_current_patient'));
    if (last) return toCtx(last);
    toast.error('No patient name found in this document. Pick the patient once and it will be remembered.');
    return null;
  };

  useEffect(() => {
    const first = Object.values(generationResult?.documents || {}).find(x => x?.data);
    if (first && !patientContext?.patientId) ensurePatient(first, false, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationResult]);

  // Fill AI-generated data into interactive template
  const handleFillTemplate = async (docType, docData) => {
    const ctx = await ensurePatient(docData);
    if (!ctx) return;
    
    try {
      const token = getToken();
      // Save the generated data to admission_forms collection for the template to load
      const payload = {
        formType: docType,
        patientId: ctx.patientId,
        ...docData.data,
        aiGenerated: true,
        generatedAt: new Date().toISOString(),
      };
      
      // Clinical forms merge the AI values on open (saved clinician values win), so no overwrite here
      if (!CLINICAL_FORMS[docType]) await fetch(`${API_URL}/api/admission-forms/save`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const templateRoute = getTemplateRoute(docType);
      if (['soc_roc', 'recertification', 'discharge', 'oasis_assessment'].includes(docType)) {
        sessionStorage.setItem('oasisE2Prefill', JSON.stringify(docData?.data || docData || {}));
      }
      if (CLINICAL_FORMS[docType]) sessionStorage.setItem(`clinicalForm:${docType}`, JSON.stringify(docData?.data || {}));
      if (templateRoute) {
        toast.success('Opening template with AI-generated data...', {
          description: 'Review and edit the information, then save.'
        });
        navigate(`${templateRoute}?patientId=${ctx.patientId}`);
      }
    } catch (error) {
      console.error('Error filling template:', error);
      toast.error('Failed to open template');
    }
  };

  // Save document to patient record and navigate to view it
  const handleSaveToPatient = async (docType, docData) => {
    const ctx = await ensurePatient(docData);
    if (!ctx) return;
    
    try {
      const token = getToken();
      const payload = {
        formType: docType,
        patientId: ctx.patientId,
        ...docData.data,
        aiGenerated: true,
        generatedAt: new Date().toISOString(),
      };
      
      const response = await fetch(`${API_URL}/api/admission-forms/save`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Document saved to patient record!', {
          description: `Saved as ${DOCUMENT_TYPES[docType]?.name || docType}. Navigating to view...`
        });
        
        // Navigate to where the document was saved
        const templateRoute = getTemplateRoute(docType);
        if (templateRoute) {
          setTimeout(() => {
            if (['soc_roc', 'recertification', 'discharge', 'oasis_assessment'].includes(docType)) {
              sessionStorage.setItem('oasisE2Prefill', JSON.stringify(docData?.data || {}));
            }
            navigate(`${templateRoute}?patientId=${ctx.patientId}`);
          }, 1500);
        } else {
          // If no specific template, go to patient demographics
          setTimeout(() => {
            navigate(`/dashboard/patients/demographics?patientId=${ctx.patientId}`);
          }, 1500);
        }
      } else {
        toast.error('Failed to save document');
      }
    } catch (error) {
      console.error('Error saving to patient:', error);
      toast.error('Failed to save document');
    }
  };

  // Handle New Patient Documentation Upload - Creates patient and generates all docs
  const handleNewPatientUpload = async () => {
    if (!selectedFile) {
      toast.error('Please upload a document first');
      return;
    }
    
    setGenerating(true);
    
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('selectedTemplates', JSON.stringify([
        'facesheet', 'poc_485', 'soc_roc', 
        'nurses_notes', 'oasis_assessment', 'medication_list', 'pt_evaluation', 'ot_evaluation', 'st_evaluation', 'msw_evaluation', 'dietitian_evaluation', 'wound_watfs',
        'wound_care_order', 'emergency_info', 'billing_agreement', 'consent_verification', 'home_assessment',
        'admission_consent_exact', 'admission_packet_exact'
      ]));
      
      toast.info('Extracting patient information from document...', {
        description: 'This may take a minute'
      });
      
      // Use the comprehensive patient upload endpoint
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout
      
      const response = await fetch(`${API_URL}/api/ai/comprehensive-patient-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const result = await response.json();
      
      console.log('Upload response:', result); // Debug log
      
      if (!response.ok) {
        toast.error(`Server error: ${result.detail || result.error || 'Unknown error'}`);
        return;
      }
      
      if (result.success && result.patientId) {
        toast.success('New patient created successfully!', {
          description: `${result.patientName || 'Patient'} has been added with all admission documents.`
        });
        
        // Set the patient context
        setPatientContext({
          patientId: result.patientId,
          patientName: result.patientName || 'New Patient',
          mrn: result.mrn || result.patientId?.slice(0, 8) || '',
        });
        
        // Show generated documents
        if (result.generatedDocuments) {
          setGeneratedDocs(result.generatedDocuments);
        }
        
        // Navigate to the patient's admission packet to show all generated forms
        setTimeout(() => {
          navigate(`/dashboard/admission/index?patientId=${result.patientId}`);
        }, 2000);
        
      } else {
        toast.error(result.error || result.detail || 'Failed to create patient');
      }
      
    } catch (error) {
      console.error('Error creating new patient:', error);
      if (error.name === 'AbortError') {
        toast.error('Request timed out. Please try with a smaller file or try again.');
      } else {
        toast.error(`Failed to create patient: ${error.message || 'Network error'}`);
      }
    } finally {
      setGenerating(false);
      setSelectedFile(null);
    }
  };

  const handleDocTypeToggle = (docType) => {
    setSelectedDocTypes(prev => 
      prev.includes(docType)
        ? prev.filter(t => t !== docType)
        : [...prev, docType]
    );
  };

  const handleSelectAllDocTypes = () => {
    if (selectedDocTypes.length === VISIBLE_TYPES.length) {
      setSelectedDocTypes([]);
    } else {
      setSelectedDocTypes(VISIBLE_TYPES);
    }
  };

  const handlePatientSelect = async (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    if (patient) {
      autoPicked.current = false;
      localStorage.setItem('ivory_current_patient', patient.id);
      // Set basic patient context
      setPatientContext({
        patientId: patient.id,
        patientName: `${patient.firstName} ${patient.lastName}`,
        mrn: patient.medicalRecordNumber || patient.id?.slice(0, 8) || '',
        dateOfBirth: patient.dateOfBirth || '',
        primaryDiagnosis: patient.primaryDiagnosis || '',
        // Additional comprehensive fields
        address: patient.address || '',
        city: patient.city || '',
        state: patient.state || '',
        zipCode: patient.zipCode || '',
        phone: patient.phone || '',
        insurance: patient.insurance || '',
        insuranceMemberId: patient.insuranceMemberId || patient.medicareNumber || '',
        insuranceGroupNumber: patient.insuranceGroupNumber || '',
        secondaryDiagnoses: patient.secondaryDiagnoses || [],
        allergies: patient.allergies || [],
        medications: patient.currentMedications || [],
        primaryPhysician: patient.primaryPhysician || '',
        primaryPhysicianNPI: patient.primaryPhysicianNPI || '',
        primaryPhysicianPhone: patient.primaryPhysicianPhone || '',
        primaryPhysicianAddress: patient.primaryPhysicianAddress || '',
        physicianEmail: patient.physicianEmail || patient.primaryPhysicianEmail || '',
        physicianFax: patient.physicianFax || patient.primaryPhysicianFax || '',
        homeboundReason: patient.homeboundReason || '',
        startOfCareDate: patient.startOfCareDate || '',
        gender: patient.gender || '',
        functionalStatus: patient.functionalStatus || '',
        cognitiveStatus: patient.cognitiveStatus || '',
        chart: patient
      });
      setPdfPatient(patient);
      
      // Also check for auto-populated 485 data
      try {
        const token = getToken();
        const response = await fetch(`${API_URL}/api/forms/poc-485/auto-populated/${patientId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success && data.form) {
          toast.success('Auto-populated 485 data found! Data loaded.');
          // Merge the 485 form data into patient context
          const form = data.form;
          setPatientContext(prev => ({
            ...prev,
            certPeriodStart: form.certPeriodStart || prev.startOfCareDate,
            certPeriodEnd: form.certPeriodEnd,
            ...(form.formData || {})
          }));
        }
      } catch (error) {
        // Silent fail - just means no auto-populated data
        console.log('No auto-populated 485 data found');
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'text/plain', 'text/csv', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/png', 'image/jpeg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Unsupported file type. Please upload PDF, TXT, CSV, DOCX, PNG, or JPG.');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleGenerate = async () => {
    if (selectedDocTypes.length === 0) {
      toast.error('Please select at least one document type to generate');
      return;
    }

    if (activeTab === 'text' && !sourceText.trim()) {
      toast.error('Please enter source text (e.g., skilled nursing notes)');
      return;
    }

    if (activeTab === 'file' && !selectedFile) {
      toast.error('Please upload a source document');
      return;
    }

    setGenerating(true);
    setGenerationResult(null);
    if (autoPicked.current) {
      autoPicked.current = false;
      setPatientContext({ patientName: '', mrn: '', dateOfBirth: '', primaryDiagnosis: '' });
    }

    try {
      const token = getToken();
      let response;

      if (activeTab === 'text') {
        // Generate from text
        response = await fetch(`${API_URL}/api/document-generator/from-text`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sourceText: sourceText,
            documentTypes: selectedDocTypes,
            patientContext: patientContext
          })
        });
      } else {
        // Generate from file
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('document_types', JSON.stringify(selectedDocTypes));
        formData.append('patient_context', JSON.stringify(patientContext));

        response = await fetch(`${API_URL}/api/document-generator/from-file`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      }

      let data;
      try {
        data = await response.json();
      } catch {
        toast.error(`Server returned an unexpected response (status ${response.status}). Please try again.`);
        return;
      }

      if (data.jobId) {
        toast.info(activeTab === 'text' ? 'Generating... OASIS SOC / Discharge can take 1-2 minutes.' : 'Reading your document... large or scanned files can take a few minutes.');
        const started = Date.now();
        while (Date.now() - started < 10 * 60 * 1000) {
          await new Promise(r => setTimeout(r, 4000));
          const jobRes = await fetch(`${API_URL}/api/document-generator/jobs/${data.jobId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!jobRes.ok) continue;
          const job = await jobRes.json();
          if (job.status === 'completed') {
            data = { success: true, generationId: job.generationId, result: job.result };
            break;
          }
          if (job.status === 'failed') {
            data = { success: false, detail: job.error };
            break;
          }
        }
        if (data.jobId) {
          toast.error('Document generation is taking too long. Please check Generation History shortly.');
          return;
        }
      }
      
      if (data.success) {
        // Check if the LLM result indicates an error
        if (data.result && data.result.success === false) {
          if (data.result.error_type === 'budget_exceeded') {
            toast.error('LLM Budget Exceeded! Please add balance via Profile → Universal Key → Add Balance', { duration: 8000 });
          } else {
            toast.error(data.result.error || 'Document generation failed');
          }
          setGenerationResult(data.result);
        } else {
          setGenerationResult(data.result);
          toast.success('Documents generated successfully!');
          // Expand all generated documents by default
          const expanded = {};
          selectedDocTypes.forEach(dt => expanded[dt] = true);
          setExpandedDocs(expanded);
        }
      } else {
        toast.error(data.detail || 'Failed to generate documents');
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(`Could not reach the server: ${error.message}. Please try again.`);
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (!regenPending) return;
    setRegenPending(false);
    toast.info('Regenerating documents from the saved input...');
    handleGenerate().then(fetchHistory);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenPending]);


  const toggleDocExpand = (docType) => {
    setExpandedDocs(prev => ({
      ...prev,
      [docType]: !prev[docType]
    }));
  };

  // one tap: replace the order's medications with the patient's saved medication list
  const importMeds = async (docType) => {
    if (!patientContext?.patientId) { toast.error('Select the patient first'); return; }
    const r = await fetch(`${API_URL}/api/medications/patient/${patientContext.patientId}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    const meds = r.ok ? (await r.json()).medications || [] : [];
    const active = meds.filter(m => !/discontinued/i.test(m.status || '') && (!m.endDate || new Date(m.endDate) >= new Date()));
    if (!active.length) { toast.error('No saved medications for this patient'); return; }
    const saved = active.map(m => ({ name: m.name, dose: [m.dosage, m.strength].filter(Boolean).join(' '), route: m.route, frequency: [m.frequency, m.isPRN && 'PRN'].filter(Boolean).join(' '),
      instructions: [m.instructions, m.usedFor && `For ${m.usedFor}`].filter(Boolean).join('. ') }));
    // keep medications already on the order (e.g. a new prescription) and add saved ones not yet listed
    const current = generationResult?.documents?.[docType]?.data?.medications;
    const existing = (Array.isArray(current) ? current : []).filter(x => x && typeof x === 'object' && x.name);
    const key = (n) => String(n || '').toLowerCase().split(/\s+/)[0];
    const added = saved.filter(s => !existing.some(e => key(e.name) === key(s.name)));
    const rows = [...existing, ...added];
    setGenerationResult(prev => ({ ...prev, documents: { ...prev.documents, [docType]: { ...prev.documents[docType], data: { ...prev.documents[docType].data, medications: rows } } } }));
    toast.success(added.length ? `Added ${added.length} medication${added.length > 1 ? 's' : ''} from the patient's list` : 'All saved medications are already on this order');
  };

  const renderDocumentResult = (docType, docData) => {
    if (!docData) return null;
    const config = DOCUMENT_TYPES[docType];
    const isExpanded = expandedDocs[docType];
    const Icon = config?.icon || FileText;

    return (
      <div key={docType} className="border rounded-lg overflow-hidden mb-3" data-testid={`doc-result-${docType}`}>
        <div 
          className={`flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 ${config?.color || 'bg-gray-100'}`}
          onClick={() => toggleDocExpand(docType)}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5" />
            <div>
              <h4 className="font-semibold">{docData.name || config?.name}</h4>
              {docData.confidence_score !== undefined && (
                <span className="text-xs">
                  Confidence: {docData.confidence_score}%
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {docData.generated ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            )}
            {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </div>
        </div>
        
        {isExpanded && docData.data && (
          <div className="p-4 bg-white border-t">
            {ORDER_TYPES[docType] && (
              <Button size="sm" className="mb-3 mr-2 bg-[#d4af37] text-gray-900 hover:bg-[#c9a227]" onClick={() => {
                if (!patientContext?.patientId) { toast.error('Select the patient first'); return; }
                setSignOrder({ docType, data: docData.data });
              }} data-testid={`send-order-sign-${docType}`}>
                <Send className="w-4 h-4 mr-1" /> Send to doctor for signature
              </Button>
            )}
            {MED_ORDERS.includes(docType) && (
              <Button size="sm" variant="outline" className="mb-3" onClick={() => importMeds(docType)} data-testid={`import-meds-${docType}`}>
                <Pill className="w-4 h-4 mr-1" /> Use patient's saved medication list
              </Button>
            )}
            {docData.missing_info && docData.missing_info.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm font-medium text-yellow-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Missing Information
                </p>
                <ul className="mt-2 text-sm text-yellow-700">
                  {docData.missing_info.map((info, idx) => (
                    <li key={idx}>{clinicalLabel(docType, info) || info}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="space-y-3">
              {typeof docData.data === 'object' ? (
                Object.entries(docData.data).map(([key, value]) => (
                  <div key={key} className="grid grid-cols-3 gap-2 border-b pb-2">
                    <span className="text-sm font-medium text-gray-600 capitalize">
                      {clinicalLabel(docType, key) || key.replace(/NPI|ICD10/g, m => ` ${m === 'ICD10' ? 'ICD-10' : m}`).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ')}:
                    </span>
                    <span className="col-span-2 text-sm text-gray-800">
                      {value === null || value === '' || (Array.isArray(value) && value.length === 0) ? (
                        <span className="text-gray-400 italic">Not available</span>
                      ) : isDisciplineOrders(key, value) ? (
                        <DisciplineOrdersTable value={value} testId={`doc-field-orders-${key}`} />
                      ) : isStringList(value) ? (
                        <BulletList items={value} testId={`doc-field-bullets-${key}`} />
                      ) : value && typeof value === 'object' && !Array.isArray(value) ? (
                        Object.values(value).filter(Boolean).join(', ')
                      ) : Array.isArray(value) && value.every(v => v && typeof v === 'object') ? (
                        <ul className="space-y-1" data-testid={`doc-field-list-${key}`}>
                          {value.map((item, i) => (
                            <li key={i} className="bg-gray-50 rounded px-2 py-1">
                              {Object.values(item).filter(v => v && typeof v !== 'object').join(', ')}
                            </li>
                          ))}
                        </ul>
                      ) : typeof value === 'object' ? (
                        JSON.stringify(value, null, 2)
                      ) : (
                        String(value)
                      )}
                    </span>
                  </div>
                ))
              ) : (
                <pre className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">
                  {String(docData.data)}
                </pre>
              )}
            </div>
            
            <div className="mt-4 flex gap-2 flex-wrap">
              {/* Fill into Template Button - Goes to interactive form */}
              {getTemplateRoute(docType) && (
                <Button 
                  variant="default" 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFillTemplate(docType, docData);
                  }}
                  data-testid={`fill-template-${docType}`}
                >
                  <FileText className="w-4 h-4 mr-1" /> Fill into Template
                </Button>
              )}
              
              {/* Save to Patient Record */}
              <Button 
                variant="default" 
                size="sm" 
                className="bg-purple-600 hover:bg-purple-700"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveToPatient(docType, docData);
                }}
                data-testid={`save-to-patient-${docType}`}
              >
                <Save className="w-4 h-4 mr-1" /> Save & View
              </Button>
              
              <Button variant="outline" size="sm" onClick={() => {
                const jsonStr = JSON.stringify(docData.data, null, 2);
                const blob = new Blob([jsonStr], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${docType}_${new Date().toISOString().split('T')[0]}.json`;
                a.click();
              }}>
                <Download className="w-4 h-4 mr-1" /> Export JSON
              </Button>
              <Button 
                variant="default" 
                size="sm" 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const mode = await chooseFillMode();
                    if (!mode) return;
                    const ctx = (await ensurePatient(docData, true, false)) || patientContext;
                    setNextFillMode(mode);
                    downloadBlob(await buildDocPdfBlob(docType, docData, ctx), pdfFilename(docType));
                    toast.success(`PDF exported: ${config?.name || docType}`);
                  } catch (err) {
                    toast.error(err.message);
                  }
                }}
                data-testid={`export-pdf-${docType}`}
              >
                <Printer className="w-4 h-4 mr-1" /> Export PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                onClick={async (e) => {
                  e.stopPropagation();
                  const ctx = await ensurePatient(docData);
                  if (!ctx) return;
                  try {
                    const blob = await buildDocPdfBlob(docType, docData, ctx);
                    await savePdfToPackages(ctx.patientId, blob, pdfFilename(docType), `${config?.name || docType} (AI generated)`);
                    toast.success(`${config?.name || docType} saved to ${ctx.patientName}'s Application Packages`);
                  } catch (err) {
                    toast.error(`Could not save to packages: ${err.message}`);
                  }
                }}
                data-testid={`save-to-packages-${docType}`}
              >
                <FolderPlus className="w-4 h-4 mr-1" /> Save to Packages
              </Button>
              {docType === 'poc_485' && (
                <Button
                  size="sm"
                  className="bg-gray-900 text-[#d4af37] hover:bg-gray-800"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (await ensurePatient(docData)) setSignatureDoc(docData);
                  }}
                  data-testid="send-for-signature-poc_485"
                >
                  <Send className="w-4 h-4 mr-1" /> Send for Signature
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#f39c12] to-[#e67e22] rounded-lg shadow-lg p-6 mb-6 text-white">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Brain className="w-10 h-10" />
            </div>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                AI Document Generator
                <Sparkles className="w-6 h-6 text-yellow-300" />
              </h1>
              <p className="text-white/90 mt-1">
                Auto-generate clinical forms from nursing notes or uploaded documents using AI
              </p>
            </div>
          </div>
        </div>

        {/* Quick Links to Fillable Templates */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Fillable Templates - Click to open interactive forms
          </h3>
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={() => navigate(patientContext?.patientId ? `/dashboard/patients/demographics?patientId=${patientContext.patientId}` : '/dashboard/patients/demographics')}
              className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors flex items-center gap-1"
            >
              <FileText className="w-4 h-4" /> Patient Demographics
            </button>
            <button 
              onClick={() => navigate(patientContext?.patientId ? `/dashboard/templates/cms-485?patientId=${patientContext.patientId}` : '/dashboard/templates/cms-485')}
              className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 transition-colors flex items-center gap-1"
            >
              <FileText className="w-4 h-4" /> CMS-485 Plan of Care
            </button>
            <button 
              onClick={() => navigate(patientContext?.patientId ? `/dashboard/templates/face-sheet?patientId=${patientContext.patientId}` : '/dashboard/templates/face-sheet')}
              className="px-3 py-2 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600 transition-colors flex items-center gap-1"
            >
              <FileText className="w-4 h-4" /> Patient Face Sheet
            </button>
            <button 
              onClick={() => navigate(patientContext?.patientId ? `/dashboard/forms/soc-roc?patientId=${patientContext.patientId}` : '/dashboard/forms/soc-roc')}
              className="px-3 py-2 bg-pink-500 text-white rounded-lg text-sm hover:bg-pink-600 transition-colors flex items-center gap-1"
            >
              <FileText className="w-4 h-4" /> SOC/ROC Assessment
            </button>
            <button 
              onClick={() => navigate(patientContext?.patientId ? `/dashboard/clinical/medication-profile?patientId=${patientContext.patientId}` : '/dashboard/clinical/medication-profile')}
              className="px-3 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 transition-colors flex items-center gap-1"
            >
              <FileText className="w-4 h-4" /> Medication Profile
            </button>
            <button 
              onClick={() => navigate(patientContext?.patientId ? `/dashboard/skilled-nursing/nurses-notes?patientId=${patientContext.patientId}` : '/dashboard/skilled-nursing/nurses-notes')}
              className="px-3 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 transition-colors flex items-center gap-1"
            >
              <FileText className="w-4 h-4" /> Nursing Notes
            </button>
          </div>
          {!patientContext?.patientId && (
            <p className="text-xs text-blue-600 mt-2">
              💡 Tip: Select a patient below to auto-fill their information in these templates
            </p>
          )}
        </div>

        {/* Quick Templates Section */}
        <div className="bg-white rounded-lg shadow-sm border mb-6 overflow-hidden" data-testid="quick-templates-section">
          <button
            onClick={() => setShowTemplates(!showTemplates)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg">
                <Sparkles className="w-5 h-5 text-amber-600" />
              </div>
              <div className="text-left">
                <h2 className="font-semibold text-gray-800">Quick Templates</h2>
                <p className="text-sm text-gray-500">Generate complete document packages with one click</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedTemplate && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedTemplate.startsWith('custom_') 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {selectedTemplate.startsWith('custom_') 
                    ? customTemplates.find(t => `custom_${t.id}` === selectedTemplate)?.name 
                    : DOCUMENT_TEMPLATES[selectedTemplate]?.name}
                </span>
              )}
              {showTemplates ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
            </div>
          </button>
          
          {showTemplates && (
            <div className="p-4 border-t bg-gray-50">
              {selectedTemplate && (
                <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${
                  selectedTemplate.startsWith('custom_') 
                    ? 'bg-purple-50 border border-purple-200' 
                    : 'bg-amber-50 border border-amber-200'
                }`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">
                      {selectedTemplate.startsWith('custom_') 
                        ? customTemplates.find(t => `custom_${t.id}` === selectedTemplate)?.icon 
                        : DOCUMENT_TEMPLATES[selectedTemplate]?.icon}
                    </span>
                    <div>
                      <p className={`font-medium ${selectedTemplate.startsWith('custom_') ? 'text-purple-800' : 'text-amber-800'}`}>
                        {selectedTemplate.startsWith('custom_') 
                          ? customTemplates.find(t => `custom_${t.id}` === selectedTemplate)?.name 
                          : DOCUMENT_TEMPLATES[selectedTemplate]?.name} Selected
                      </p>
                      <p className={`text-sm ${selectedTemplate.startsWith('custom_') ? 'text-purple-600' : 'text-amber-600'}`}>
                        {selectedDocTypes.length} documents will be generated
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={clearTemplate} 
                    className={selectedTemplate.startsWith('custom_') ? 'text-purple-700 border-purple-300' : 'text-amber-700 border-amber-300'}
                  >
                    Clear Selection
                  </Button>
                </div>
              )}
              
              {/* Preset Templates */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Preset Templates
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {Object.entries(DOCUMENT_TEMPLATES).map(([key, template]) => (
                    <button
                      key={key}
                      onClick={() => applyTemplate(key)}
                      className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                        selectedTemplate === key 
                          ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200' 
                          : 'border-gray-200 bg-white hover:border-amber-300'
                      }`}
                      data-testid={`template-${key}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{template.icon}</span>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-800 text-sm truncate">{template.name}</h3>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{template.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                              {template.documents.length} docs
                            </span>
                            <span className="text-xs text-gray-400">
                              ~{template.estimatedTime}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Templates */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-500" /> My Custom Templates
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSaveTemplateModal(true)}
                    disabled={selectedDocTypes.length === 0}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    data-testid="save-template-btn"
                  >
                    <Save className="w-4 h-4 mr-1" /> Save Current Selection
                  </Button>
                </div>
                
                {customTemplates.length === 0 ? (
                  <div className="text-center py-6 bg-white rounded-lg border-2 border-dashed border-gray-200">
                    <Star className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">No custom templates yet</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Select document types and click "Save Current Selection" to create one
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {customTemplates.map(template => (
                      <div
                        key={template.id}
                        className={`relative p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                          selectedTemplate === `custom_${template.id}` 
                            ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' 
                            : 'border-gray-200 bg-white hover:border-purple-300'
                        }`}
                        data-testid={`custom-template-${template.id}`}
                      >
                        <button
                          onClick={() => applyTemplate(template.id, true)}
                          className="w-full text-left"
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">{template.icon}</span>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-800 text-sm truncate">{template.name}</h3>
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{template.description || 'Custom template'}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-xs px-2 py-0.5 bg-purple-100 rounded-full text-purple-600">
                                  {template.documents.length} docs
                                </span>
                                {template.usageCount > 0 && (
                                  <span className="text-xs text-gray-400">
                                    Used {template.usageCount}x
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this template?')) {
                              deleteCustomTemplate(template.id);
                            }
                          }}
                          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Save Template Modal */}
        {showSaveTemplateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="save-template-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 m-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">Save Custom Template</h2>
                <button onClick={() => setShowSaveTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="font-semibold">Template Name *</Label>
                  <input
                    type="text"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    placeholder="e.g., My Agency's Admission Set"
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    data-testid="template-name-input"
                  />
                </div>
                
                <div>
                  <Label className="font-semibold">Description (Optional)</Label>
                  <textarea
                    value={newTemplateDescription}
                    onChange={(e) => setNewTemplateDescription(e.target.value)}
                    placeholder="Describe what this template is for..."
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    rows={2}
                  />
                </div>
                
                <div>
                  <Label className="font-semibold">Icon</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {templateIcons.map(icon => (
                      <button
                        key={icon}
                        onClick={() => setNewTemplateIcon(icon)}
                        className={`w-10 h-10 text-xl rounded-lg border-2 transition-all ${
                          newTemplateIcon === icon 
                            ? 'border-amber-500 bg-amber-50' 
                            : 'border-gray-200 hover:border-amber-300'
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Selected Documents ({selectedDocTypes.length})</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedDocTypes.map(dt => (
                      <span key={dt} className="px-2 py-1 bg-white rounded text-xs border">
                        {DOCUMENT_TYPES[dt]?.name || dt}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowSaveTemplateModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={saveCustomTemplate} 
                  disabled={savingTemplate || !newTemplateName.trim()}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  {savingTemplate ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" /> Save Template
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Input */}
          <div className="lg:col-span-2 space-y-6">
            {/* Source Selection Tabs */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="flex border-b">
                <button
                  className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
                    activeTab === 'text' 
                      ? 'bg-[#f39c12] text-white' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveTab('text')}
                  data-testid="tab-text"
                >
                  <Wand2 className="w-4 h-4" />
                  From Nursing Notes
                </button>
                <button
                  className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
                    activeTab === 'file' 
                      ? 'bg-[#f39c12] text-white' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveTab('file')}
                  data-testid="tab-file"
                >
                  <Upload className="w-4 h-4" />
                  From Document Upload
                </button>
                <button
                  className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
                    activeTab === 'newpatient' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveTab('newpatient')}
                  data-testid="tab-newpatient"
                >
                  <Plus className="w-4 h-4" />
                  New Patient Documentation
                </button>
              </div>

              <div className="p-4">
                {activeTab === 'newpatient' ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h3 className="font-semibold text-green-800 flex items-center gap-2 mb-2">
                        <Plus className="w-5 h-5" />
                        Create New Patient & Generate Documentation
                      </h3>
                      <p className="text-sm text-green-700 mb-4">
                        Upload a document (referral, face sheet, hospital discharge) and AI will extract patient information to create a new patient record and generate all admission documents automatically.
                      </p>
                      
                      <div 
                        className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                          selectedFile ? 'border-green-400 bg-green-100' : 'border-green-300 hover:border-green-500 hover:bg-green-100'
                        }`}
                        onClick={() => !selectedFile && document.getElementById('new-patient-file-input').click()}
                      >
                        {selectedFile ? (
                          <div className="space-y-2">
                            <FileUp className="w-12 h-12 mx-auto text-green-600" />
                            <p className="font-medium text-green-700">{selectedFile.name}</p>
                            <p className="text-sm text-gray-500">
                              {(selectedFile.size / 1024).toFixed(1)} KB
                            </p>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFile(null);
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Upload className="w-12 h-12 mx-auto text-green-500" />
                            <p className="text-green-700 font-medium">
                              Upload patient document to create new patient
                            </p>
                            <p className="text-sm text-green-600">
                              PDF, TXT, DOCX, or image of referral/face sheet
                            </p>
                          </div>
                        )}
                        <input
                          id="new-patient-file-input"
                          type="file"
                          accept=".pdf,.txt,.csv,.docx,.png,.jpg,.jpeg"
                          onChange={handleFileSelect}
                          className="hidden"
                          data-testid="new-patient-file-input"
                        />
                      </div>
                      
                      <div className="mt-4 p-3 bg-white rounded-lg border border-green-200">
                        <h4 className="font-medium text-gray-800 mb-2">AI will extract and create:</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Patient Demographics
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Insurance Information
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Emergency Contacts
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Physician Information
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Diagnosis & Allergies
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            All Admission Forms
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        onClick={handleNewPatientUpload}
                        disabled={!selectedFile || generating}
                        className="w-full mt-4 bg-green-600 hover:bg-green-700"
                        data-testid="create-new-patient-btn"
                      >
                        {generating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Creating Patient & Generating Documents...
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-2" />
                            Create New Patient & Generate All Documents
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : activeTab === 'text' ? (
                  <div className="space-y-4">
                    <div>
                      <Label className="font-semibold">Source Text</Label>
                      <p className="text-sm text-gray-500 mb-2">
                        Paste skilled nursing notes, assessment summaries, or other clinical documentation
                      </p>
                      <textarea
                        value={sourceText}
                        onChange={(e) => setSourceText(e.target.value)}
                        placeholder="Enter or paste nursing notes, assessment findings, patient observations, vital signs, diagnoses, medications, etc..."
                        className="w-full h-64 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#f39c12] focus:border-transparent resize-none"
                        data-testid="source-text-input"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label className="font-semibold">Upload Document</Label>
                      <p className="text-sm text-gray-500 mb-2">
                        Upload PDF, TXT, CSV, DOCX, or image files containing clinical information
                      </p>
                      <div 
                        className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                          selectedFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-[#f39c12] hover:bg-orange-50'
                        }`}
                        onClick={() => !selectedFile && document.getElementById('file-upload-input').click()}
                      >
                        {selectedFile ? (
                          <div className="space-y-2">
                            <FileUp className="w-12 h-12 mx-auto text-green-600" />
                            <p className="font-medium text-green-700">{selectedFile.name}</p>
                            <p className="text-sm text-gray-500">
                              {(selectedFile.size / 1024).toFixed(1)} KB
                            </p>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFile(null);
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Upload className="w-12 h-12 mx-auto text-gray-400" />
                            <p className="text-gray-600 font-medium">
                              Click to upload or drag and drop
                            </p>
                            <p className="text-sm text-gray-400">
                              PDF, TXT, CSV, DOCX, PNG, JPG (Max 10MB)
                            </p>
                          </div>
                        )}
                        <input
                          id="file-upload-input"
                          type="file"
                          accept=".pdf,.txt,.csv,.docx,.png,.jpg,.jpeg"
                          onChange={handleFileSelect}
                          className="hidden"
                          data-testid="file-upload-input"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Patient Context */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#f39c12]" />
                Patient Context (Optional)
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Provide additional patient information to improve document accuracy
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-sm">Select Existing Patient</Label>
                  <select
                    value={patientContext.patientId || ''}
                    onChange={(e) => handlePatientSelect(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    data-testid="patient-select"
                  >
                    <option value="">-- Select Patient --</option>
                    {patients.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.firstName} {p.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm">Patient Name</Label>
                  <input
                    type="text"
                    value={patientContext.patientName}
                    onChange={(e) => setPatientContext(prev => ({ ...prev, patientName: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label className="text-sm">Medical Record #</Label>
                  <input
                    type="text"
                    value={patientContext.mrn}
                    onChange={(e) => setPatientContext(prev => ({ ...prev, mrn: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    placeholder="MRN-12345"
                  />
                </div>
                <div>
                  <Label className="text-sm">Date of Birth</Label>
                  <input
                    type="date"
                    value={patientContext.dateOfBirth}
                    onChange={(e) => setPatientContext(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm">Primary Diagnosis</Label>
                  <input
                    type="text"
                    value={patientContext.primaryDiagnosis}
                    onChange={(e) => setPatientContext(prev => ({ ...prev, primaryDiagnosis: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    placeholder="e.g., CHF, Diabetes"
                  />
                </div>
              </div>
            </div>

            {/* Generation Results */}
            {generationResult && (
              <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="generation-results">
                {/* Budget Exceeded Error */}
                {generationResult.success === false && generationResult.error_type === 'budget_exceeded' ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-16 h-16 mx-auto text-orange-500 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Budget Exceeded</h3>
                    <p className="text-gray-600 mb-4 max-w-md mx-auto">
                      Your Universal Key balance has been exceeded. Please add more balance to continue generating documents.
                    </p>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 inline-block">
                      <p className="text-sm text-orange-800 font-medium">
                        Go to: <span className="font-bold">Profile → Universal Key → Add Balance</span>
                      </p>
                      <p className="text-xs text-orange-600 mt-1">
                        You can also enable auto top-up to avoid interruptions
                      </p>
                    </div>
                  </div>
                ) : generationResult.success === false ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Generation Failed</h3>
                    <p className="text-gray-600 mb-4">{generationResult.error}</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        Generated Documents
                      </h3>
                      <div className="flex gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={async () => {
                            try {
                              const response = await fetch(`${API_URL}/api/ai/save-generated-documents`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                                },
                                body: JSON.stringify({
                                  documents: generationResult.documents,
                                  patientContext: patientContext,
                                  generatedAt: new Date().toISOString()
                                })
                              });
                              if (response.ok) {
                                toast.success('Documents saved successfully!');
                              } else {
                                toast.error('Failed to save documents');
                              }
                            } catch (error) {
                              toast.error('Failed to save documents');
                            }
                          }}
                          data-testid="save-documents-btn"
                        >
                          <Save className="w-4 h-4 mr-1" /> Save All
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={async () => {
                            const first = Object.values(generationResult.documents || {}).find(x => x?.data);
                            const ctx = (first && await ensurePatient(first, true, false)) || patientContext;
                            await generateAllPDFs(generationResult.documents, ctx);
                          }}
                          data-testid="export-all-pdfs"
                        >
                          <Printer className="w-4 h-4 mr-1" /> Export PDFs
                        </Button>
                      </div>
                    </div>
                    {generationResult.extraction_notes && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                        <strong>Notes:</strong> {generationResult.extraction_notes}
                      </div>
                    )}
                    <div className="space-y-2">
                      {generationResult.documents && Object.entries(generationResult.documents).map(([docType, docData]) => 
                        renderDocumentResult(docType, docData)
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Document Types & Actions */}
          <div className="space-y-6">
            {/* Document Types Selection */}
            <div className="bg-white rounded-lg shadow-sm border p-4 max-h-[600px] overflow-y-auto">
              <div className="flex items-center justify-between mb-3 sticky top-0 bg-white pb-2 border-b">
                <div>
                  <h3 className="font-semibold text-gray-800">Documents to Generate</h3>
                  {selectedTemplate && (
                    <p className="text-xs text-amber-600 mt-0.5">
                      Using: {DOCUMENT_TEMPLATES[selectedTemplate]?.name}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {selectedTemplate && (
                    <button
                      onClick={clearTemplate}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={handleSelectAllDocTypes}
                    className="text-sm text-[#f39c12] hover:underline"
                  >
                    {selectedDocTypes.length === VISIBLE_TYPES.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>
              
              {/* Group documents by category: titles and the documents under each are alphabetical */}
              {Object.entries(DOCUMENT_CATEGORIES).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(([categoryKey, category]) => {
                if (categoryKey === 'staff_hub') return (
                  <div key={categoryKey} className={`mb-4 rounded-lg border ${category.color} overflow-hidden`} data-testid="doccat-staff_hub">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-black/5 bg-white/60">
                      <h4 className="text-xs font-bold uppercase tracking-wide text-gray-700">{category.name}</h4>
                      <span className="text-[11px] font-medium text-gray-500">filled from the Time Clock</span>
                    </div>
                    <div className="p-2 space-y-1.5">
                      {Object.entries(STAFF_DOCS).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(([key, d]) => (
                        <button key={key} type="button" onClick={() => navigate(`/dashboard/staff-hub/${key}`)} data-testid={`staffdoc-${key}`}
                          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md border border-gray-200 bg-white hover:border-[#f39c12] text-left">
                          <div className="p-1.5 rounded shrink-0 bg-slate-100 text-slate-700"><FileText className="w-3 h-3" /></div>
                          <p className="flex-1 min-w-0 text-xs font-medium leading-snug text-gray-800">{d.name}</p>
                          <ChevronRight className="w-3 h-3 text-gray-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
                const categoryDocs = Object.entries(DOCUMENT_TYPES).filter(([_, config]) => config.category === categoryKey && !config.hidden)
                  .sort((a, b) => a[1].name.localeCompare(b[1].name));
                if (categoryDocs.length === 0) return null;
                const picked = categoryDocs.filter(([k]) => selectedDocTypes.includes(k)).length;
                
                return (
                  <div key={categoryKey} className={`mb-4 rounded-lg border ${category.color} overflow-hidden`} data-testid={`doccat-${categoryKey}`}>
                    <div className="flex items-center justify-between px-3 py-2 border-b border-black/5 bg-white/60">
                      <h4 className="text-xs font-bold uppercase tracking-wide text-gray-700">{category.name}</h4>
                      <span className="text-[11px] font-medium text-gray-500">{picked}/{categoryDocs.length}</span>
                    </div>
                    <div className="p-2 space-y-1.5">
                      {categoryDocs.map(([key, config]) => {
                        const Icon = config.icon;
                        const isFromTemplate = selectedTemplate && DOCUMENT_TEMPLATES[selectedTemplate]?.documents.includes(key);
                        return (
                          <label
                            key={key}
                            className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md cursor-pointer border transition-colors bg-white ${
                              selectedDocTypes.includes(key)
                                ? isFromTemplate 
                                  ? 'border-amber-500 ring-1 ring-amber-300 bg-amber-50'
                                  : 'border-[#f39c12] ring-1 ring-[#f39c12]'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid={`doctype-${key}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedDocTypes.includes(key)}
                              onChange={() => handleDocTypeToggle(key)}
                              className="w-4 h-4 shrink-0 rounded border-gray-300 text-[#f39c12] focus:ring-[#f39c12]"
                            />
                            <div className={`p-1.5 rounded shrink-0 ${config.color}`}>
                              <Icon className="w-3 h-3" />
                            </div>
                            <p className="flex-1 min-w-0 text-xs font-medium leading-snug text-gray-800">{config.name}</p>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="mt-4 pt-4 border-t">
                <Button
                  onClick={handleGenerate}
                  disabled={generating || selectedDocTypes.length === 0}
                  className="w-full bg-[#f39c12] hover:bg-[#e67e22] disabled:opacity-50"
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate Documents
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-gray-500 mt-2">
                  Powered by Gemini 3 Flash AI
                </p>
              </div>
            </div>

            <SignatureTracker compact refreshKey={signatureDoc ? 1 : 0} />

            {/* Generation History */}
            <div className="bg-white rounded-lg shadow-sm border">
              <button
                onClick={() => {
                  setShowHistory(!showHistory);
                  if (!showHistory && history.length === 0) {
                    fetchHistory();
                  }
                }}
                data-testid="generation-history-toggle"
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <span className="font-semibold text-gray-800 flex items-center gap-2">
                  <History className="w-5 h-5 text-[#f39c12]" />
                  Generation History
                </span>
                {showHistory ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              </button>
              
              {showHistory && (
                <div className="border-t p-4">
                  {loadingHistory ? (
                    <div className="text-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    </div>
                  ) : history.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No generation history yet
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {history.map(item => (
                        <div
                          key={item.id}
                          role="button"
                          tabIndex={0}
                          title="Click to open these documents"
                          onClick={() => openHistoryItem(item)}
                          onKeyDown={(e) => e.key === 'Enter' && openHistoryItem(item)}
                          data-testid={`history-item-${item.id}`}
                          className="group p-3 bg-gray-50 rounded-lg text-sm cursor-pointer border border-transparent hover:border-[#f39c12] hover:bg-orange-50 transition-colors"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium capitalize flex items-center gap-1">
                              {item.sourceType === 'file' ? <FileText className="w-4 h-4 text-gray-500 shrink-0" /> : <FileType className="w-4 h-4 text-gray-500 shrink-0" />}
                              {item.sourceType === 'file' ? item.fileName : 'Text Input'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(item.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="mt-2 flex justify-end gap-2">
                            <button type="button" onClick={(e) => { e.stopPropagation(); openHistoryItem(item); }}
                              data-testid={`history-open-${item.id}`}
                              className="flex items-center gap-1 px-2 py-1 rounded bg-white border text-xs font-medium hover:border-[#f39c12] hover:text-[#f39c12]">
                              <Eye className="w-3 h-3" /> Open
                            </button>
                            <button type="button" disabled={generating} onClick={(e) => { e.stopPropagation(); regenerateHistoryItem(item); }}
                              data-testid={`history-regenerate-${item.id}`}
                              className="flex items-center gap-1 px-2 py-1 rounded bg-white border text-xs font-medium hover:border-[#f39c12] hover:text-[#f39c12] disabled:opacity-40">
                              <RefreshCw className="w-3 h-3" /> Regenerate
                            </button>
                          </div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {item.documentTypes?.map(dt => (
                              <span key={dt} className="px-2 py-0.5 bg-gray-200 rounded text-xs">
                                {DOCUMENT_TYPES[dt]?.name || dt}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {signatureDoc && (
        <SendForSignatureModal docData={signatureDoc} patientContext={patientContext} onClose={() => setSignatureDoc(null)} />
      )}
      {signOrder && (
        <SendOrderSignModal patientId={patientContext?.patientId} orderType={ORDER_TYPES[signOrder.docType]} docType={signOrder.docType}
          defaults={{ physicianName: signOrder.data?.orderingPhysician || signOrder.data?.physicianName || patientContext?.primaryPhysician, physicianEmail: patientContext?.physicianEmail, physicianFax: patientContext?.physicianFax }}
          getPdfBlob={async () => {
            await loadAgencyLogo();
            const blank = { physicianSignature: '', physicianSignatureDate: '', nurseSignature: '', nurseSignatureDate: '' };
            const d = buildOrderPdf(ORDER_TYPES[signOrder.docType], { ...(signOrder.data || {}), ...blank }, patientContext || {}, ORDER_KIND[signOrder.docType]);
            d.fillMode = 'flat';
            return d.output('blob');
          }}
          onClose={() => setSignOrder(null)} />
      )}
    </DashboardLayout>
  );
};

export default DocumentGeneratorPage;
